/*                                                                             */
/* Components to render the "Overview" tab on Specimen Details page            */
/* Contents:                                                                   */
/* * SpecimenCombinedBasicInfoCard: Age, Girth, Height combo (used on mobile)  */
/* * SpecimenBasicInfoCard: Single card, shows either Age, Girth or Height     */
/* * SpecimenCombinedEcoServiceCard: Ecosystem services combo on single card   */
/* * SpecimenEcoserviceCard: Single card, shows a single ecoservice ranking    */
/* * NatureCulture: Single card, shows either Nature or Culture section    */
/* * SpecimenOverviewTab: content of the "Overview" tab on Specimen page       */
/*                                                                             */

import AgeIcon from 'assets/icons/commonsIcon/age_icon.svg?react';
import BiodiversityIndexIcon from 'assets/icons/commonsIcon/biodiversity_index_icon.svg?react';
import CarbonStorageIcon from 'assets/icons/commonsIcon/carbon_storage_icon.svg?react';
import CoolingPowerIcon from 'assets/icons/commonsIcon/cooling_power_icon.svg?react';
import GirthIconDesk from 'assets/icons/commonsIcon/girth_icon_desk.svg?react';
import GirthiconMobile from 'assets/icons/commonsIcon/girth_icon_mobile.svg?react'; // TODO temporary solution, if use same svg component for both desktop and mobile, svg don't render expected, due to non-unique id.
import HeightIcon from 'assets/icons/commonsIcon/height_icon.svg?react';
import AddedByReviewedByCard from 'components/BioRegion/AddedByReviewedByCard';
import MultilingualHtmlParser from 'components/Multilingual/MultilingualHtmlParser';
import ContextHelp from 'components/common/ContextHelp';
import { saveAs } from 'file-saver';
import IntlMessages from 'helpers/IntlMessages';
import { specimenEcosystemServices } from 'helpers/ecosystem-services/specimenEcosystemServices';
import { getTranslatedString, roundIfFloat } from 'helpers/utils-typescript';
import PaperclipIcon from 'mdi-react/PaperclipIcon';
import React, { Fragment, ReactElement, ReactNode, useMemo } from 'react';
import { Card, Container, Row } from 'react-bootstrap';
import { FormattedDate } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Speciman,
  SpecimenUploadedResource
} from 'services/graphqltypes';

interface EcoServiceInfo {
  statisticContent: any,
  titleTranslationId: string,
  iconDesktop: ReactElement<{ height: string, className: string, fill: 'string' }>,
  iconMobile: ReactElement<{ height: string, className: string, fill: 'string' }>,
  showIndex?: boolean,
}

/** Helper function: Get the contextHelpId depending on the type of ecoservice being displayed */
const getEcoServiceContextHelpId = (ecoServiceTitleTranslationId: string) => {

  switch (ecoServiceTitleTranslationId) {
    case 'specimen.kg-co2-fixed-per-year':
      return '90f70aa8-e062-4a48-9043-0d00b6a8eb56';
    case 'specimen.kg-co2-fixed-life-time':
      return '486167ed-c2a7-4fa8-b609-98f82c99ac33';
    case 'specimen.kwh-cooled-per-year':
      return '0400c248-6e59-4015-a42c-5fd0f2f3d172';
    case 'specimen.biodiversity-richness':
      return '2c196383-d88f-4689-94c7-af71116728ff';
    default:
      break;
  }


};


/** Single card: Age, Girth, Height combo (used on mobile)  */
const SpecimenCombinedBasicInfoCard = ({
  basicInfoArray,
  contextHelpId,
}: {
  basicInfoArray: {
    statisticContent: any,
    titleTranslationId: string,
    Icon: ReactElement<{ height: string, className: string }>
  }[],
  contextHelpId: string,
}) => {
  return (
    <Card>
      <Card.Body className='px-3 py-2'>
        <div className='row no-gutters'>
          <div className='h4 my-2 mx-2 color-theme-1'>
            <IntlMessages id={'specimen.basic-info'} />
          </div>
          <div className='ml-auto'>
            <ContextHelp contextHelpId={contextHelpId} />
          </div>
        </div>

      </Card.Body>

      <Card.Body className='px-3 pt-1'>
        <div className="d-flex flex-row justify-content-center">
          {
            basicInfoArray.map((basicInfo, index) => {
              return (
                <Fragment key={index}>
                  <div className="mx-auto w-30 d-flex flex-column">
                    <div className='d-flex flex-row align-items-end mx-auto mb-2' style={{ maxWidth: '100%' }}>
                      <div>
                        {basicInfo.Icon}
                      </div>
                      <div className="color-theme-1 h4 align-bottom m-0 ">
                        <IntlMessages id={basicInfo.titleTranslationId} />
                      </div>
                    </div>
                    <div className="text-center text-truncate" style={{ maxWidth: '100%' }}>
                      {basicInfo.statisticContent}
                    </div>
                  </div>

                  {/* Separator, don't show behind the last statistic block */}
                  {(index + 1)! < basicInfoArray.length &&
                    <div className="mx-1" style={{ borderLeft: '1px solid #D3D3D3' }} />}
                </Fragment>
              );
            }
            )}
        </div>
      </Card.Body>
    </Card>

  );
};

/** Single card, shows either Age, Girth or Height */
const SpecimenBasicInfoCard = ({ statisticContent, titleTranslationId, contextHelpId, children }: {
  /** The main icon is passed as a child component */
  children: ReactNode,
  /** The number shown as "X/10" (or a string, we use "?") */
  statisticContent: number | string,
  /** Title is shown under the number */
  titleTranslationId: string,
  /** ID of the connected context help */
  contextHelpId: string,
}) => {
  return (
    <Card className='my-1'>
      <Card.Body className='px-2 py-1'>
        <div className='float-right'>
          <ContextHelp contextHelpId={contextHelpId} />
        </div>
      </Card.Body>
      <Card.Body className='px-2 pt-0'>
        <div className='d-flex flex-nowrap no-gutters align-items-center' style={{ maxWidth: '100%' }}>
          <div className='col d-flex justify-content-end ml-auto'>
            <div>
              {children}
            </div>
          </div>
          <div className='col-7 pl-4 pl-md-2 mr-auto' style={{ minWidth: '0' }}> {/* if an element styled with 'text-truncate' is not direct child of a flex element, then its parent component must have min-width = 0 or some actual value */}
            <div className='mr-auto h4 mb-0 color-theme-1'>
              <IntlMessages id={titleTranslationId} />
            </div>
            <div className='mr-auto text-truncate' style={{ maxWidth: '100%' }}>
              {statisticContent}
            </div>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

/** Ecosystem services combo on single card */
const SpecimenCombinedEcoserviceCard = ({
  ecoServiceInfoArray,
  contextHelpId,
}: {
  ecoServiceInfoArray: EcoServiceInfo[],
  contextHelpId: string,
}) => {
  return (
    <Card className='my-1'>
      <Card.Body className='px-3 py-2'>
        <div className='row no-gutters'>
          <div className='h4 my-2 mx-2 color-theme-1'>
            <IntlMessages id={'specimen.ecosystem-service'} />
          </div>
          <div className='ml-auto'>
            <ContextHelp contextHelpId={contextHelpId} />
          </div>
        </div>
      </Card.Body>

      <Card.Body className='pt-0 px-2'>
        <div className='d-flex flex-column mt-n1'>

          {ecoServiceInfoArray.map((ecoServiceInfo, index) => {
            return (
              ecoServiceInfo.statisticContent &&
              <div key={index} className='d-flex flex-row flex-nowrap align-items-center my-2'>
                <div className='mx-auto w-30 d-flex justify-content-center py-1'>
                  {ecoServiceInfo.iconMobile}
                </div>
                <div className='mx-auto text-left w-30 px-2'>
                  <IntlMessages id={ecoServiceInfo.titleTranslationId} />
                </div>
                <div className='mx-auto h2 text-center mb-0 w-30'>
                  {ecoServiceInfo.showIndex ? `${ecoServiceInfo.statisticContent} / 10` : roundIfFloat(Number(ecoServiceInfo.statisticContent), 0)}
                </div>
              </div>
            );
          })}

        </div>

      </Card.Body>

    </Card>
  );

};

/** Single card, shows a single ecoservice ranking */
const SpecimenEcoserviceCard = ({
  ecoServiceInfo,
  contextHelpId,
}: {
  ecoServiceInfo: EcoServiceInfo,
  contextHelpId: string,
}) => {
  return (
    <Card className='my-1' style={{ height: '100%' }}>
      <Card.Body className='px-2 py-1'>
        <div className='float-right'>
          <ContextHelp contextHelpId={contextHelpId} />
        </div>
      </Card.Body>
      <Card.Body className='px-2 pt-0'>
        <div className='d-flex flex-column '>
          <div className='mx-auto mb-3 mt-2'>
            {ecoServiceInfo.iconDesktop}
          </div>
          <div className='mx-auto display-4 text-center' style={{ wordBreak: 'break-all' }}>
            {ecoServiceInfo.showIndex ? `${ecoServiceInfo.statisticContent} / 10` : roundIfFloat(Number(ecoServiceInfo.statisticContent), 0)}
          </div>
          <div className='mx-auto text-center'>
            <IntlMessages id={ecoServiceInfo.titleTranslationId} />
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};


/** Single card, shows either Nature or Culture section + its uploaded resources  */
export const NatureCulture = ({ titleTranslationId, description, specimenResources, contextHelpId }: {
  /** translation ID of the card title */
  titleTranslationId: string,
  /** will be passed into MultilingualHtmlParser, is multilingual html */
  description: any,
  /** uploaded files, shown under the main content section */
  specimenResources: SpecimenUploadedResource[],
  /** ID of the context help linked to (i) icon in top right corner */
  contextHelpId: string,
}) => {

  const currentLang = useSelector((state: any) => state.settings.locale);
  const saveFile = (url: string, fileName: string) => {
    saveAs(url, fileName);
  };

  return (
    <Fragment>
      <Card.Body className='px-4 py-2'>
        <div className='row no-gutters'>
          <div className='h4 my-2 color-theme-1'>
            <IntlMessages id={titleTranslationId} />
          </div>
          <div className='ml-auto'>
            <ContextHelp contextHelpId={contextHelpId} />
          </div>
        </div>
      </Card.Body>
      <Card.Body className='pt-0 px-4'>
        <div className="text-normal html-parser">
          <MultilingualHtmlParser multilingualHtml={description} />
        </div>
      </Card.Body>
      {/* separator */}
      {
        specimenResources.length > 0 &&
        <div className="border-top mt-2">

          <Card.Body className='pb-3 mt-3 pt-0' >

            {specimenResources?.map((uploadedResource, index: number) => {
              const uploadedResourceUrl = uploadedResource.uploadedResource.url;
              const uploadedResourceName = getTranslatedString(uploadedResource.name, currentLang);
              return (
                <p className="mb-2 ml-2" key={index}>
                  <a href={uploadedResourceUrl} className="color-theme-1-underline-link"
                    onClick={(event) => {
                      event.preventDefault();
                      saveFile(uploadedResourceUrl, uploadedResourceName);
                    }}
                  >
                    <PaperclipIcon color="gray" size="20" className="mr-2" />{uploadedResourceName}
                  </a>
                </p>
              );
            })}

          </Card.Body>
        </div>
      }
    </Fragment>
  );
};

/** Content of the "Overview tab on Specimen Details page" */
const SpecimenOverviewTab = ({ specimen }: {
  specimen: Speciman,
}): React.ReactElement => {

  const { specimenNatureResources, specimenCultureResources }: {
    specimenNatureResources: SpecimenUploadedResource[],
    specimenCultureResources: SpecimenUploadedResource[]
  } = useMemo(() => {
    const specimenNatureResources = specimen?.specimenUploadedResourcesBySpecimenId?.
      nodes.filter((resource: SpecimenUploadedResource) =>
        resource.type === 'NATURE') || [];

    const specimenCultureResources = specimen?.specimenUploadedResourcesBySpecimenId?.
      nodes.filter((resource: SpecimenUploadedResource) =>
        resource.type === 'CULTURE') || [];

    return { specimenNatureResources, specimenCultureResources };

  }, [specimen]
  );

  const {
    height: specimenHeight,
    age: specimenAge,
    co2FixationPerYear: specimenCo2FixationPerYear,
    co2FixationLifeTime: specimenCo2FixationLifeTime,
    // coolingByTranspiration: specimenCoolingByTranspiration, // for now we are switching to coolingByShading from iTree
    coolingByShading: specimenCoolingByShading,
  } = specimenEcosystemServices(
    specimen.species?.scientificName,
    specimen.girthCm,
    specimen.surfaceSealing,
    specimen.itreeApiJson
  );

  const ecoServiceInfoArray = useMemo(() => {
    const ecoServiceInfoArray = [];
    if (specimenCo2FixationPerYear) {
      ecoServiceInfoArray.push({
        statisticContent: specimenCo2FixationPerYear,
        titleTranslationId: 'specimen.kg-co2-fixed-per-year',
        iconDesktop: <CarbonStorageIcon height={'5rem'} fill='rgba(224, 78, 32, 0.6)' />,
        iconMobile: <CarbonStorageIcon height={'4rem'} fill='rgba(224, 78, 32, 0.6)' />,
      });
    }
    if (specimenCo2FixationLifeTime) {
      ecoServiceInfoArray.push({
        statisticContent: specimenCo2FixationLifeTime,
        titleTranslationId: 'specimen.kg-co2-fixed-life-time',
        iconDesktop: <CarbonStorageIcon height={'5rem'} />,
        iconMobile: <CarbonStorageIcon height={'4rem'} />,
      });
    }
    if (specimenCoolingByShading) {
      ecoServiceInfoArray.push({
        statisticContent: specimenCoolingByShading,
        titleTranslationId: 'specimen.kwh-cooled-per-year',
        iconDesktop: <CoolingPowerIcon height={'5rem'} />,
        iconMobile: <CoolingPowerIcon height={'4rem'} />,
      });
    }
    if (specimen.biodiversityRichness) {
      ecoServiceInfoArray.push({
        statisticContent: specimen.biodiversityRichness,
        titleTranslationId: 'specimen.biodiversity-richness',
        iconDesktop: <BiodiversityIndexIcon height={'5rem'} />,
        iconMobile: <BiodiversityIndexIcon height={'4rem'} />,
        showIndex: true,
      });
    }
    return ecoServiceInfoArray;
  }, [specimenCoolingByShading,
    specimenCo2FixationPerYear,
    specimenCo2FixationLifeTime,
    specimen.biodiversityRichness]);


  return (
    <Fragment>

      {/* Basic info */}
      {specimen.type !== 'POI' && // doesn't show for POIs
        <>
          <Container className='pb-1 mt-3'>

            {/* mobile view */}
            <Row className='mx-n4 d-md-none'>
              <div className='col-12 px-1'>
                <SpecimenCombinedBasicInfoCard
                  basicInfoArray={[
                    { statisticContent: `${specimenAge || specimen.ageYears} years`, titleTranslationId: 'specimen.age', Icon: <AgeIcon height={'1.5rem'} className='mr-2' /> },
                    { statisticContent: `${specimen.girthCm} cm`, titleTranslationId: 'specimen.girth', Icon: <GirthiconMobile height={'1.5rem'} className='mr-2' title='girthiconMobile' /> },
                    { statisticContent: `${specimenHeight} m`, titleTranslationId: 'specimen.height', Icon: <HeightIcon height={'1.5rem'} className='mr-2' /> },
                  ]}
                  contextHelpId='b0b4f8f8-1e7b-4019-a8c2-7f6be55d9858' />
              </div>
            </Row>

            {/* desktop view */}
            <Row className='d-none d-md-flex align-items-start'>
              {/* last measured at */}
              <div className='mt-n2 mt-md-0 w-100' style={{ textAlign: 'end' }}>
                <IntlMessages id="specimen.last-measured" />: &nbsp;{
                  specimen?.lastMeasuredAt
                    ? <FormattedDate value={specimen?.lastMeasuredAt} year="numeric" month="short" day="2-digit" />
                    : `-`
                }
              </div>

              {/* title */}
              <div className='h3 font-weight-bold mt-2 mt-md-0 w-100'>
                <IntlMessages id={'specimen.basic-info'} />
              </div>
            </Row>

            {
              specimen.isNatureMonument &&
              <Row>
                <div className="d-none d-md-flex my-2 alert alert-warning w-100" style={{ 'color': '#856404' }} role="alert">
                  <IntlMessages id={'specimen.imported-box'} />
                </div>
              </Row>
            }

            <Row className='d-none d-md-flex my-2 mx-n4'>
              {/* age */}
              <div className='col-12 col-md-4 px-lg-3 px-1'>
                <SpecimenBasicInfoCard statisticContent={`${specimenAge || specimen.ageYears || '?'} years old`} titleTranslationId='specimen.age' contextHelpId='97d860ca-a5a1-44f5-a174-9380dafea890'>
                  <AgeIcon height={'2.25rem'} className='my-2' />
                </SpecimenBasicInfoCard>
              </div>

              {/* girth */}
              <div className='col-12 col-md-4 px-lg-3 px-1'>
                <SpecimenBasicInfoCard statisticContent={`${specimen.girthCm || '?'} cm`} titleTranslationId='specimen.girth' contextHelpId='3c716d75-065c-4d3e-a024-7f969e7ba129'>
                  <GirthIconDesk height={'2.25rem'} className='my-2' title='girthiconDesktop' />
                </SpecimenBasicInfoCard>
              </div>

              {/* height */}
              <div className='col-12 col-md-4 px-lg-3 px-1'>
                <SpecimenBasicInfoCard statisticContent={`${specimenHeight} m`} titleTranslationId='specimen.height' contextHelpId='16b77b3b-5e38-4d9d-9979-1fc549d0fd89'>
                  <HeightIcon height={'2.25rem'} className='my-2' />
                </SpecimenBasicInfoCard>
              </div>
            </Row>


          </Container>

          {/* Ecosystem services superpowers */}
          {ecoServiceInfoArray.length > 0
            &&
            <Container className='pb-1 mt-3'>

              {/* mobile view */}
              <Row className='d-md-none mx-n4'>
                <div className='col-12 px-1'>
                  <SpecimenCombinedEcoserviceCard
                    contextHelpId='cd7278ad-7c8c-40ef-91c1-0a1191036853'
                    ecoServiceInfoArray={ecoServiceInfoArray}
                  />
                </div>
              </Row>


              {/* desktop view */}
              <Row className='d-none d-md-flex h3 font-weight-bold'>
                {/* title */}
                <IntlMessages id={'specimen.ecosystem-service'} />
              </Row>

              <Row className='d-none d-md-flex my-3 mx-n4'>
                {ecoServiceInfoArray.map((ecoServiceInfo, index) => {
                  return (
                    <Fragment key={index}>
                      {(ecoServiceInfoArray.length === 4 && index === 2) && // force flex to wrap between the 2nd and 3rd card if there is 4 cards to show
                        <div className='w-100' />}
                      <div className='col-12 col-md px-lg-3 px-1 my-lg-3 my-1'>
                        <SpecimenEcoserviceCard
                          ecoServiceInfo={ecoServiceInfo}
                          contextHelpId={getEcoServiceContextHelpId(ecoServiceInfo.titleTranslationId)}
                        />
                      </div>
                    </Fragment>
                  );
                })}
              </Row>
            </Container>
          }
        </>
      }

      {/* Nature and culture  */}
      <Container>
        <Row className='my-md-3 my-1 mx-n4'>
          {/* Nature - doesn't show for POIs*/}
          {specimen.type !== 'POI' &&
            <div className='col-12 px-1 px-lg-3 my-1 my-md-0'>
              <Card className="mb-3 card-full-width-img w-100 p-2">
                <NatureCulture titleTranslationId={'species.nature'} description={specimen.natureDescription} specimenResources={specimenNatureResources} contextHelpId='cc009079-4cab-4864-9f8c-981a807cce43' />
              </Card>
            </div>
          }

          {/* Culture // For POI: Description */}
          <div className='col-12 px-1 px-lg-3 my-1 my-md-0'>
            <Card className="mb-3 card-full-width-img w-100 p-2">
              <NatureCulture
                titleTranslationId={specimen.type !== 'POI' ? 'species.culture' : 'route.quest-description'}
                description={specimen.cultureDescription}
                specimenResources={specimenCultureResources}
                contextHelpId={specimen.type !== 'POI' ? '59f06ba8-4531-40bc-862c-bf80d3839a76' : '15828c52-dd7c-4f19-b17e-9a2ce7836bc8'}
              />
            </Card>
          </div>
        </Row>
      </Container>

      {/* Added by reviewed by: mobile view only*/}
      <Container className='d-md-none pb-1 mt-3'>
        <Row className='mx-n4'>
          <div className='col-12 px-1'>
            <AddedByReviewedByCard specimanBySlug={specimen} />
          </div>
        </Row>
      </Container>

    </Fragment >
  );

};

export default SpecimenOverviewTab;
